import type { Translate, TranslationKey, TranslationValues } from "./i18n";

/**
 * Copy for system notices shown in modals. Texts follow the platform's standard wording for restrictions.
 * Standard copy is stored as translation keys and translated when the modal renders, so an open notice follows a
 * language switch; backend-provided text is kept verbatim.
 */
export type NoticeText = { key: TranslationKey; values?: TranslationValues } | { text: string };
/** `compact` uses the smaller type of the arbitrage site's notices; `closeButton: false` leaves only the support action and the corner cross. */
export type Notice = { title: NoticeText; paragraphs: NoticeText[]; support: boolean; compact?: boolean; closeButton?: boolean };

export function resolveNoticeText(value: NoticeText, t: Translate) {
  return "text" in value ? value.text : t(value.key, value.values);
}

const key = (value: TranslationKey, values?: TranslationValues): NoticeText => (values ? { key: value, values } : { key: value });

export const notices = {
  verification: {
    title: key("dashboard.notice_verification_title"),
    paragraphs: [key("dashboard.notice_verification_p1"), key("dashboard.notice_verification_p2"), key("dashboard.notice_verification_p3")],
    support: true,
    compact: true,
    closeButton: false
  },
  tax: {
    title: key("dashboard.notice_tax_title"),
    paragraphs: [key("dashboard.notice_tax_p1"), key("dashboard.notice_tax_p2"), key("dashboard.notice_tax_p3")],
    support: true,
    compact: true,
    closeButton: false
  },
  withdrawBlocked: {
    title: key("dashboard.notice_withdraw_blocked_title"),
    paragraphs: [key("dashboard.notice_withdraw_blocked_p1")],
    support: true
  },
  buyBlocked: {
    title: key("dashboard.notice_buy_blocked_title"),
    paragraphs: [key("dashboard.notice_buy_blocked_p1")],
    support: true
  },
  aml: {
    title: key("dashboard.notice_aml_title"),
    paragraphs: [key("dashboard.notice_aml_p1"), key("dashboard.notice_aml_p2"), key("dashboard.notice_aml_p3")],
    support: true
  }
} satisfies Record<string, Notice>;

/** `amount` is the already formatted threshold (e.g. `formatMoney(threshold, currency)`). */
export function largeDepositNotice(amount: string): Notice {
  return {
    title: key("dashboard.notice_large_deposit_title"),
    paragraphs: [key("dashboard.notice_large_deposit_p1", { amount })],
    support: true,
    compact: true,
    closeButton: false
  };
}
